import { useState } from 'react';
import { useRecordCourtOrder } from '../api/queries.ts';
import { useCourtOrderForm } from '../api/court-orders.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure } from './LoadFailure.tsx';
import { formatDate } from '../dates.ts';
import type {
  CourtOrderForm, CourtOrderInput, OrderPowerInput, PowerCategory, RecordedCourtOrder,
} from '../api/types.ts';

/** Today as a calendar day in the viewer's zone — never via toISOString. */
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const step = 'text-xs font-bold tracking-[0.14em] text-neutral-500 uppercase';

const HEADINGS: { key: PowerCategory; label: string }[] = [
  { key: 'PERSONAL_NEEDS', label: 'Personal needs' },
  { key: 'PROPERTY_MANAGEMENT', label: 'Property management' },
];

type Role = 'SOLE' | 'CO' | '';
type PowerDraft = { state: 'GRANTED' | 'STRUCK'; clause: string; qualifier: string; sources: string[] };

function roleLabel(role: 'SOLE' | 'CO' | null): string {
  return role === 'SOLE' ? 'sole' : role === 'CO' ? 'co-guardian' : 'not the agency’s';
}

/**
 * Record a court order, from the client's page, in the order the document is
 * read: the appointment it belongs to, the order itself, what it says about
 * each power, and anyone else it names.
 *
 * Nothing is inferred. Which sides the order grants follows from the powers
 * ticked and the court's own heading each sits under; scope, the client's
 * commission date and every deadline are derived by the schema, and the panel
 * shows what came out.
 *
 * Closed until asked for. It is not what a case manager opens a client to do.
 */
export function CourtOrderPanel({ clientId }: { clientId: string }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<RecordedCourtOrder | null>(null);
  const form = useCourtOrderForm(clientId, open);
  const record = useRecordCourtOrder(clientId);

  const close = () => { setResult(null); record.reset(); setOpen(false); };

  if (!open) {
    return (
      <div className="px-4 pb-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-h-[52px] w-full items-center justify-center rounded-lg border
                     border-neutral-300 px-4 font-semibold text-neutral-800
                     hover:bg-neutral-50 active:bg-neutral-100"
        >
          Record a court order
        </button>
      </div>
    );
  }

  if (result) {
    return <Recorded result={result} onAnother={() => { setResult(null); record.reset(); }} onDone={close} />;
  }

  if (form.error && !form.data) {
    return (
      <div className="border-t border-neutral-200">
        <LoadFailure
          what="The court order form"
          error={form.error}
          onRetry={() => void form.refetch()}
          retrying={form.isFetching}
          forbiddenIsSession={false}
        />
      </div>
    );
  }
  if (!form.data) {
    return <p className="border-t border-neutral-200 px-4 py-4 text-neutral-500">Loading the order form…</p>;
  }

  return (
    <OrderForm
      clientId={clientId}
      catalogue={form.data}
      record={record}
      onRecorded={setResult}
      onCancel={close}
    />
  );
}

function OrderForm({
  clientId,
  catalogue,
  record,
  onRecorded,
  onCancel,
}: {
  clientId: string;
  catalogue: CourtOrderForm;
  record: ReturnType<typeof useRecordCourtOrder>;
  onRecorded: (r: RecordedCourtOrder) => void;
  onCancel: () => void;
}) {
  const live = catalogue.appointments.filter((a) => a.is_live);

  // 1. the appointment
  const [appointmentId, setAppointmentId] = useState<string>(live.length === 1 ? live[0]!.id : '');
  const [indexNumber, setIndexNumber] = useState('');
  const [personRole, setPersonRole] = useState<Role>('');
  const [propertyRole, setPropertyRole] = useState<Role>('');
  const [successor, setSuccessor] = useState(false);
  const [limited, setLimited] = useState(false);
  const [trustee, setTrustee] = useState(false);

  // 2. the order
  const [orderType, setOrderType] = useState('');
  const [signed, setSigned] = useState('');
  const [term, setTerm] = useState<'temporary' | 'permanent' | null>(null);
  const [commissioned, setCommissioned] = useState('');
  const [county, setCounty] = useState('');
  const [motion, setMotion] = useState('');
  const [judge, setJudge] = useState('');

  // 3. the powers
  const [powers, setPowers] = useState<Record<string, PowerDraft>>({});

  // 4. anyone else named
  const [otherName, setOtherName] = useState('');
  const [otherNote, setOtherNote] = useState('');
  const [examiner, setExaminer] = useState('');
  // Nine orders in ten say "no less than 4 times per year", so the form opens
  // ticked and filled. The minority that say nothing are recorded by unticking.
  const [ordersVisits, setOrdersVisits] = useState(true);
  const [minVisits, setMinVisits] = useState('4');

  const isNew = appointmentId === 'new';
  const chosen = catalogue.appointments.find((a) => a.id === appointmentId);
  const sides: Record<PowerCategory, 'SOLE' | 'CO' | null> = isNew
    ? { PERSONAL_NEEDS: personRole || null, PROPERTY_MANAGEMENT: propertyRole || null }
    : { PERSONAL_NEEDS: chosen?.allied_person_role ?? null, PROPERTY_MANAGEMENT: chosen?.allied_property_role ?? null };

  const setPower = (code: string, draft: PowerDraft | null) =>
    setPowers((current) => {
      const next = { ...current };
      if (draft) next[code] = draft; else delete next[code];
      return next;
    });

  // Only powers on a side the appointment holds are sent; a side changed after
  // ticking must not carry powers the agency no longer holds.
  const heldCodes = new Set(
    catalogue.powers.filter((p) => sides[p.category] !== null).map((p) => p.code),
  );
  const drafted = Object.entries(powers).filter(([code]) => heldCodes.has(code));
  const granted = drafted.filter(([, p]) => p.state === 'GRANTED');

  const problems = [
    !appointmentId && 'choose the appointment',
    isNew && !personRole && !propertyRole && 'give the new appointment a side',
    !orderType && 'choose the order type',
    !signed && 'enter the signed date',
    term === null && 'say whether it is temporary or permanent',
    granted.length === 0 && 'tick at least one power granted',
    granted.some(([, p]) => !p.clause.trim()) && 'give each granted power its clause',
  ].filter(Boolean) as string[];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (problems.length > 0 || term === null) return;
    const input: CourtOrderInput = {
      client_id: clientId,
      appointment_id: isNew ? null : appointmentId,
      new_appointment: isNew
        ? {
            index_number: indexNumber.trim() || null,
            allied_person_role: personRole || null,
            allied_property_role: propertyRole || null,
            is_successor: successor,
            is_limited: limited,
            is_trustee: trustee,
          }
        : null,
      order_type_code: orderType,
      signed_date: signed,
      is_temporary: term === 'temporary',
      commissioned_date: commissioned || null,
      county_code: county || null,
      motion_sequence: motion.trim() || null,
      judge_name: judge.trim() || null,
      powers: drafted.map(([code, p]): OrderPowerInput => ({
        power_code: code,
        state: p.state,
        verbatim_text: p.state === 'GRANTED' ? p.clause.trim() : null,
        qualifier: p.qualifier.trim() || null,
        source_order_ids: p.state === 'GRANTED' ? p.sources : [],
      })),
      other_guardians: otherName.trim()
        ? [{ name: otherName.trim(), authority_note: otherNote.trim() || null }]
        : [],
      examiner_name: examiner.trim() || null,
      order_specifies_visits: ordersVisits,
      minimum_visits_per_year: ordersVisits && /^[0-9]+$/.test(minVisits) ? Number(minVisits) : null,
    };
    record.mutate(input, { onSuccess: onRecorded });
  };

  return (
    <form onSubmit={submit} className="space-y-5 border-t border-neutral-200 px-4 py-4">
      <p className={step}>Record a court order</p>

      {/* 1. appointment */}
      <fieldset className="space-y-2">
        <legend className={label}>1 · The appointment it belongs to</legend>
        {live.map((a) => (
          <label key={a.id}
                 className={'flex min-h-[48px] items-center gap-3 rounded-lg border px-3 py-2 ' +
                            (appointmentId === a.id ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-300')}>
            <input type="radio" name="co-appointment" checked={appointmentId === a.id}
                   onChange={() => setAppointmentId(a.id)} className="size-5 shrink-0" />
            <span className="min-w-0 text-[0.9375rem] text-neutral-900">
              <span className="font-mono">{a.index_number ?? 'No index number'}</span>
              <span className="block text-sm text-neutral-600">
                Person {roleLabel(a.allied_person_role)} · property {roleLabel(a.allied_property_role)}
                {a.is_temporary ? ' · temporary' : ''}{a.is_successor ? ' · successor' : ''}
              </span>
            </span>
          </label>
        ))}
        <label className={'flex min-h-[48px] items-center gap-3 rounded-lg border px-3 ' +
                          (isNew ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-300')}>
          <input type="radio" name="co-appointment" checked={isNew}
                 onChange={() => setAppointmentId('new')} className="size-5 shrink-0" />
          <span className="font-medium text-neutral-900">A new appointment</span>
        </label>

        {isNew && (
          <div className="space-y-3 rounded-lg bg-neutral-50 p-3">
            <div>
              <label className={label} htmlFor="co-index">Index number, as the order writes it</label>
              <input id="co-index" value={indexNumber} maxLength={48}
                     onChange={(e) => setIndexNumber(e.target.value)} className={field} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {([['Over the person', personRole, setPersonRole, 'co-person'],
                 ['Over the property', propertyRole, setPropertyRole, 'co-property']] as const)
                .map(([text, value, set, id]) => (
                  <div key={id}>
                    <label className={label} htmlFor={id}>{text}, the agency is</label>
                    <select id={id} value={value} onChange={(e) => set(e.target.value as Role)} className={field}>
                      <option value="">Not appointed on this side</option>
                      <option value="SOLE">Sole guardian</option>
                      <option value="CO">Co-guardian</option>
                    </select>
                  </div>
                ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {([['Successor', successor, setSuccessor], ['Limited', limited, setLimited], ['Trustee', trustee, setTrustee]] as const)
                .map(([text, checked, set]) => (
                  <label key={text}
                         className={'flex min-h-[44px] items-center gap-2 rounded-lg border px-3 ' +
                                    (checked ? 'border-neutral-900 bg-white' : 'border-neutral-300 bg-white')}>
                    <input type="checkbox" checked={checked} onChange={(e) => set(e.target.checked)} className="size-5" />
                    <span className="text-[0.9375rem] font-medium text-neutral-900">{text}</span>
                  </label>
                ))}
            </div>
          </div>
        )}
      </fieldset>

      {/* 2. order */}
      <fieldset className="space-y-3">
        <legend className={label}>2 · The order</legend>
        <div>
          <label className={label} htmlFor="co-type">Type of order</label>
          <select id="co-type" value={orderType} onChange={(e) => setOrderType(e.target.value)} className={field}>
            <option value="">Choose…</option>
            {catalogue.order_types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="co-signed">Signed</label>
            <input id="co-signed" type="date" value={signed} max={todayLocal()}
                   onChange={(e) => setSigned(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label} htmlFor="co-commissioned">Commissioned (if it has been)</label>
            <input id="co-commissioned" type="date" value={commissioned}
                   min={signed || undefined} max={todayLocal()}
                   onChange={(e) => setCommissioned(e.target.value)} className={field} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {(['temporary', 'permanent'] as const).map((t) => (
            <label key={t}
                   className={'flex min-h-[48px] items-center gap-2 rounded-lg border px-3 ' +
                              (term === t ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-300')}>
              <input type="radio" name="co-term" checked={term === t} onChange={() => setTerm(t)} className="size-5" />
              <span className="font-medium capitalize text-neutral-900">{t}</span>
            </label>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <label className={label} htmlFor="co-county">County</label>
            <select id="co-county" value={county} onChange={(e) => setCounty(e.target.value)} className={field}>
              <option value="">Not recorded</option>
              {catalogue.counties.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className={label} htmlFor="co-motion">Motion sequence</label>
            <input id="co-motion" value={motion} maxLength={32} placeholder="MOT SEQ 007"
                   onChange={(e) => setMotion(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label} htmlFor="co-judge">Justice</label>
            <input id="co-judge" value={judge} maxLength={128}
                   onChange={(e) => setJudge(e.target.value)} className={field} />
          </div>
        </div>
      </fieldset>

      {/* 3. powers */}
      <fieldset className="space-y-3">
        <legend className={label}>3 · What the order says about each power</legend>
        {!appointmentId && <p className="text-sm text-neutral-600">Choose the appointment first.</p>}
        {appointmentId && HEADINGS.map((h) => {
          const side = sides[h.key];
          if (side === null) {
            return (
              <p key={h.key} className="rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
                <span className="font-semibold text-neutral-800">{h.label}:</span> not the agency’s under this
                appointment. If another guardian holds it, name them in step 4.
              </p>
            );
          }
          return (
            <div key={h.key}>
              <p className="text-sm font-bold text-neutral-800">
                {h.label} <span className="font-normal text-neutral-500">· {roleLabel(side)}</span>
              </p>
              <ul className="mt-1 divide-y divide-neutral-200 rounded-lg border border-neutral-200">
                {catalogue.powers.filter((p) => p.category === h.key).map((p) => (
                  <PowerRow
                    key={p.code}
                    code={p.code}
                    label={p.label}
                    draft={powers[p.code] ?? null}
                    priorOrders={catalogue.prior_orders}
                    onChange={(d) => setPower(p.code, d)}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </fieldset>

      {/* 4. others */}
      <fieldset className="space-y-3">
        <legend className={label}>4 · Anyone else the order names</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="co-other">Another guardian</label>
            <input id="co-other" value={otherName} maxLength={200} placeholder="Surname, Forename"
                   onChange={(e) => setOtherName(e.target.value)} className={field} />
          </div>
          <div>
            <label className={label} htmlFor="co-other-note">What they hold</label>
            <input id="co-other-note" value={otherNote} maxLength={500}
                   placeholder="Sole guardian of the property"
                   onChange={(e) => setOtherNote(e.target.value)} className={field} />
          </div>
        </div>
        <div>
          <label className={label} htmlFor="co-examiner">Court examiner</label>
          <input id="co-examiner" value={examiner} maxLength={200} placeholder="Surname, Forename"
                 onChange={(e) => setExaminer(e.target.value)} className={field} />
        </div>

        {/*
          Ticked and filled with 4, because about nine orders in ten say "to
          visit the AIP/IP no less than 4 times per year" under obligations.
          A different number is typed over it. An order that says NOTHING about
          visiting is recorded by unticking — and then no court minimum is
          written and the client follows the agency rate for where they live.

          The tick is what makes the number mean something. Without it, an
          untouched 4 would be stored as a court requirement for every client
          whose order is silent, which is inventing a legal fact.
        */}
        <div>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={ordersVisits} className="mt-1 size-5 rounded border-neutral-400"
                   onChange={(e) => setOrdersVisits(e.target.checked)} />
            <span className={label}>The order specifies a minimum number of visits</span>
          </label>

          {ordersVisits ? (
            <>
              <label className={`${label} mt-2`} htmlFor="co-min-visits">
                Minimum visits per year as per court order
              </label>
              <input id="co-min-visits" type="number" min={1} max={52} step={1} value={minVisits}
                     onChange={(e) => setMinVisits(e.target.value)} className={field} />
              <p className="mt-1 text-xs text-neutral-500">
                As the order words it, in times per year. Usually four.
              </p>
            </>
          ) : (
            <p className="mt-1 text-xs text-neutral-500">
              No court minimum will be recorded. This client will follow the agency rate
              for the kind of place they live in.
            </p>
          )}
        </div>
      </fieldset>

      {record.error && (
        <div role="alert" className="rounded-lg border-l-8 border-critical-edge bg-critical-bg
                                     px-4 py-3 text-critical-fg">
          <p className="font-semibold">{record.error.message}</p>
          {record.error instanceof ApiError && record.error.hint && (
            <p className="mt-1 text-[0.9375rem] opacity-95">{record.error.hint}</p>
          )}
        </div>
      )}

      {problems.length > 0 && (
        <p className="text-sm text-neutral-600">To record it: {problems.join('; ')}.</p>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={record.isPending || problems.length > 0}
          className="min-h-[52px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                     active:bg-neutral-700 disabled:opacity-50"
        >
          {record.isPending ? 'Recording…' : 'Record order'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[52px] rounded-lg border border-neutral-300 px-4 font-semibold
                     text-neutral-700 active:bg-neutral-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function PowerRow({
  code,
  label: text,
  draft,
  priorOrders,
  onChange,
}: {
  code: string;
  label: string;
  draft: PowerDraft | null;
  priorOrders: CourtOrderForm['prior_orders'];
  onChange: (d: PowerDraft | null) => void;
}) {
  const value = draft?.state ?? '';
  return (
    <li className="px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 text-[0.9375rem] text-neutral-900">{text}</span>
        <label className="sr-only" htmlFor={`co-power-${code}`}>{text}</label>
        <select
          id={`co-power-${code}`}
          value={value}
          onChange={(e) => {
            const state = e.target.value;
            if (!state) onChange(null);
            else onChange({ clause: '', qualifier: '', sources: [], ...draft, state: state as PowerDraft['state'] });
          }}
          className="min-h-[44px] rounded-lg border border-neutral-300 bg-white px-2 text-[0.9375rem] text-neutral-800"
        >
          <option value="">Not on the order</option>
          <option value="GRANTED">Granted</option>
          <option value="STRUCK">Struck</option>
        </select>
      </div>

      {draft?.state === 'GRANTED' && (
        <div className="mt-2 space-y-2">
          <div>
            <label className="block text-sm text-neutral-600" htmlFor={`co-clause-${code}`}>
              The clause, as the order words it
            </label>
            <textarea
              id={`co-clause-${code}`}
              value={draft.clause}
              rows={2}
              maxLength={4000}
              onChange={(e) => onChange({ ...draft, clause: e.target.value })}
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-base text-neutral-900"
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-600" htmlFor={`co-qualifier-${code}`}>
              Qualifier, if the order limits it
            </label>
            <input
              id={`co-qualifier-${code}`}
              value={draft.qualifier}
              maxLength={500}
              placeholder="only with prior court approval"
              onChange={(e) => onChange({ ...draft, qualifier: e.target.value })}
              className={field}
            />
          </div>
          {priorOrders.length > 0 && (
            <details>
              <summary className="flex min-h-[44px] cursor-pointer items-center text-sm font-semibold text-neutral-700">
                Granted by reference to an earlier order{draft.sources.length > 0 ? ` · ${draft.sources.length}` : ''}
              </summary>
              <ul className="space-y-1">
                {priorOrders.map((po) => (
                  <li key={po.id}>
                    <label className="flex min-h-[44px] items-center gap-2 text-sm text-neutral-800">
                      <input
                        type="checkbox"
                        className="size-5"
                        checked={draft.sources.includes(po.id)}
                        onChange={(e) =>
                          onChange({
                            ...draft,
                            sources: e.target.checked
                              ? [...draft.sources, po.id]
                              : draft.sources.filter((s) => s !== po.id),
                          })}
                      />
                      <span>
                        {po.label}, signed {formatDate(po.signed_date)}
                        {po.court_index_number && <span className="font-mono"> · {po.court_index_number}</span>}
                        {!po.granted_to_agency && ' · another guardian’s'}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </li>
  );
}

function Recorded({
  result,
  onAnother,
  onDone,
}: {
  result: RecordedCourtOrder;
  onAnother: () => void;
  onDone: () => void;
}) {
  return (
    <section className="border-t border-neutral-200 px-4 py-4" aria-live="polite">
      <p className={step}>Order recorded</p>
      <p className="mt-1 font-semibold text-neutral-900">
        {result.client?.scope_label ?? 'No appointment scope'}
      </p>
      <p className="mt-0.5 text-sm text-neutral-600">
        {result.order.is_temporary ? 'Temporary' : 'Permanent'} order signed{' '}
        {formatDate(result.order.signed_date)}
        {result.appointment.index_number && ` · ${result.appointment.index_number}`}
        {result.appointment.created && ' · new appointment'}
        {result.client?.date_commissioned &&
          ` · commissioned ${formatDate(result.client.date_commissioned)}`}
      </p>
      <p className="mt-0.5 text-sm text-neutral-600">
        {result.powers.granted} power{result.powers.granted === 1 ? '' : 's'} granted
        {result.powers.struck > 0 && `, ${result.powers.struck} struck`}
        {result.powers.by_reference > 0 && `, ${result.powers.by_reference} by reference`}
        {result.other_guardians.length > 0 &&
          ` · other guardian ${result.other_guardians.map((g) => g.full_name).join(', ')}`}
        {result.examiner && ` · court examiner ${result.examiner.full_name}`}
      </p>

      <h3 className={`mt-4 ${step}`}>
        {result.deadlines.length === 0
          ? 'No new deadlines'
          : `${result.deadlines.length} new deadline${result.deadlines.length > 1 ? 's' : ''}`}
      </h3>
      {result.deadlines.length > 0 && (
        <ul className="mt-1 divide-y divide-neutral-200">
          {result.deadlines.map((d) => (
            <li key={d.id} className="flex items-baseline gap-3 py-2">
              <span className="min-w-0 text-[0.9375rem] text-neutral-800">{d.label}</span>
              <span className="ml-auto shrink-0 text-right text-sm text-neutral-600 tabular-nums">
                {d.is_provisional ? 'about ' : ''}{formatDate(d.due_date)}
                {d.is_provisional && (
                  <span className="block text-xs text-neutral-500">estimate — period not confirmed</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={onAnother}
          className="min-h-[48px] flex-1 rounded-lg border border-neutral-300 px-4 font-semibold
                     text-neutral-700 active:bg-neutral-100"
        >
          Record another
        </button>
        <button
          type="button"
          onClick={onDone}
          className="min-h-[48px] flex-1 rounded-lg bg-neutral-900 px-4 font-semibold text-white
                     active:bg-neutral-700"
        >
          Done
        </button>
      </div>
    </section>
  );
}
